import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Share, Check, Info, MoreVertical, Monitor } from 'lucide-react';
import chopperSwapLogo from '@/assets/chopperswap-logo-new.png';
import { usePwaEnabled } from '@/hooks/usePwaEnabled';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;
};

const isPreviewEnv = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const AuthInstallPrompt = () => {
  const pwaEnabled = usePwaEnabled();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const isPreview = isPreviewEnv();

  useEffect(() => {
    if (isStandalone()) {
      setIsInstalled(true);
      return;
    }

    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedDate = new Date(wasDismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 30) {
        setDismissed(true);
      }
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDismissed(true);
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error('Install prompt error:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  if (!isPreview && (!pwaEnabled || isInstalled || dismissed)) return null;

  const canInstall = isPreview || !!deferredPrompt || isIOS();

  const benefits = [
    'Instant push notifications',
    'Works offline anywhere',
    'Quick access from home screen'
  ];

  return (
    <div className="w-full max-w-md mx-auto mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="auth-light-card force-light-theme p-6 relative">
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-muted-foreground/50 hover:text-foreground transition-colors p-1.5 rounded-full hover:bg-muted/30"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {/* iOS Instructions Modal */}
        {showIOSInstructions && (
           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-6" onClick={() => setShowIOSInstructions(false)}>
            <div className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-sm max-h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="px-4 pt-4 pb-2 flex items-center gap-3 border-b border-border/50">
                <img src={chopperSwapLogo} alt="" className="w-6 h-6 object-contain brightness-0 dark:invert" />
                <h4 className="font-semibold text-foreground text-base">Install on iOS</h4>
                <button onClick={() => setShowIOSInstructions(false)} className="ml-auto text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-full hover:bg-muted/30">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-4 space-y-2">
                <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">1</span>
                  <div>
                    <p className="font-semibold text-foreground text-sm">Tap Share</p>
                    <p className="text-muted-foreground text-xs mt-0.5">Tap the <span className="inline-flex items-center bg-primary/10 rounded-full p-1 mx-0.5 align-middle"><Share className="h-3.5 w-3.5 text-primary" /></span> button in Safari's toolbar</p>
                  </div>
                </div>
                <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">2</span>
                  <div>
                    <p className="font-semibold text-foreground text-sm">Add to Home Screen</p>
                    <p className="text-muted-foreground text-xs mt-0.5">Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                  </div>
                </div>
                <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">3</span>
                  <div>
                    <p className="font-semibold text-foreground text-sm">Confirm & Launch</p>
                    <p className="text-muted-foreground text-xs mt-0.5">Tap <strong>"Add"</strong> to install ChopperSwap</p>
                  </div>
                </div>
              </div>
              <div className="px-4 pb-4 pt-2 sticky bottom-0 bg-background">
                 <Button onClick={() => setShowIOSInstructions(false)} className="w-full">Got it!</Button>
              </div>
            </div>
          </div>
        )}

        {/* How to Install Modal */}
        {showHowTo && (
           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-6" onClick={() => setShowHowTo(false)}>
            <div className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-sm max-h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="px-4 pt-4 pb-2 flex items-center gap-3 border-b border-border/50">
                <img src={chopperSwapLogo} alt="" className="w-6 h-6 object-contain brightness-0 dark:invert" />
                <h4 className="font-semibold text-foreground text-base">How to Install</h4>
                <button onClick={() => setShowHowTo(false)} className="ml-auto text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-full hover:bg-muted/30">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-4 space-y-2">
                {isIOS() ? (
                  <>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">1</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Open in Safari</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Open this page in <strong>Safari</strong> browser</p>
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">2</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Tap Share</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Tap the <span className="inline-flex items-center bg-primary/10 rounded-full p-1 mx-0.5 align-middle"><Share className="h-3.5 w-3.5 text-primary" /></span> Share button</p>
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">3</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Add to Home Screen</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Tap <strong>"Add to Home Screen"</strong> then <strong>"Add"</strong></p>
                      </div>
                    </div>
                  </>
                ) : isAndroid() ? (
                  <>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">1</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Open Browser Menu</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Tap the <span className="inline-flex items-center bg-primary/10 rounded-full p-1 mx-0.5 align-middle"><MoreVertical className="h-3.5 w-3.5 text-primary" /></span> menu button</p>
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">2</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Tap Install</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Select <strong>"Install App"</strong> or <strong>"Add to Home Screen"</strong></p>
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">3</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Confirm & Launch</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Tap <strong>"Install"</strong> to confirm</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">1</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Find Install Icon</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Look for the <span className="inline-flex items-center bg-primary/10 rounded-full p-1 mx-0.5 align-middle"><Monitor className="h-3.5 w-3.5 text-primary" /></span> icon in the address bar</p>
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">2</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Select Install</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Or open <span className="inline-flex items-center bg-primary/10 rounded-full p-1 mx-0.5 align-middle"><MoreVertical className="h-3.5 w-3.5 text-primary" /></span> menu → <strong>"Install ChopperSwap"</strong></p>
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-2.5 flex items-start gap-3">
                      <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">3</span>
                      <div>
                        <p className="font-semibold text-foreground text-sm">Confirm & Launch</p>
                        <p className="text-muted-foreground text-xs mt-0.5">Click <strong>"Install"</strong> to add to your desktop</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="px-4 pb-4 pt-2 sticky bottom-0 bg-background">
                 <Button onClick={() => setShowHowTo(false)} className="w-full">Got it!</Button>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex items-center gap-5 pr-6">
          {/* Phone mockup */}
          <div className="flex-shrink-0">
            <div className="relative w-[72px] h-[140px] bg-slate-900 rounded-[20px] p-[3px] shadow-xl border border-slate-700/50">
              <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-8 h-[5px] bg-slate-800 rounded-full" />
              <div className="w-full h-full bg-gradient-to-br from-primary/10 via-background to-blue-500/10 dark:from-slate-200 dark:via-slate-100 dark:to-slate-300 rounded-[17px] flex items-center justify-center overflow-hidden bg-slate-300">
                <img src={chopperSwapLogo} alt="ChopperSwap" className="w-12 h-12 brightness-0 object-cover" />
              </div>
              <div className="absolute bottom-[4px] left-1/2 -translate-x-1/2 w-10 h-[3px] bg-slate-600 rounded-full" />
            </div>
          </div>

          {/* Text content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground text-lg leading-tight">Get ChopperSwap</h3>
              <button
                onClick={() => setShowHowTo(true)}
                className="text-muted-foreground/60 hover:text-primary transition-colors p-1 rounded-full hover:bg-muted/30"
                aria-label="How to install"
              >
                <Info className="h-4 w-4" />
              </button>
            </div>
            <p className="text-muted-foreground text-sm mt-0.5">on Your Device</p>
            <ul className="mt-3 space-y-1.5">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Button
          onClick={handleInstall}
          disabled={!canInstall}
          className="w-full mt-5 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
        >
          <Download className="h-4 w-4 mr-2" />
          Install App
        </Button>
      </div>
    </div>
  );
};
